import React, { useEffect, useState } from "react";
import httpService from "../../services/http.service";
import { useDebouncedEffect, useDidUpdateEffect } from "../../hooks";
import Table from "./table";
import { stableSort, deepFind } from "./sort-helpers";
import { useDispatch, useSelector } from "react-redux";
import { setRowsPerPage } from "../../redux/app";
/**
 * @param {Object} props
 *
 * @param {Object[]} props.actions
 * @param {string} props.actions[].tooltip
 * @param {Object} props.actions[].icon
 * @param {requestCallback} props.actions[].modalContent
 * @param {requestCallback} props.actions[].onClick
 *
 * @param {Object[]} props.headCells
 * @param {string} props.headCells[].field
 * @param {string} props.headCells[].title
 * @param {boolean} props.headCells[].mainField
 * @param {boolean} props.headCells[].disableSort
 * @param {requestCallback} props.headCells[].renderer
 *
 * @param {Object[]} props.rowActions
 * @param {string} props.rowActions[].tooltip
 * @param {Object | requestCallback} props.rowActions[].icon
 * @param {requestCallback} props.rowActions[].modalContent
 * @param {requestCallback} props.rowActions[].onClick
 *
 * @param {Object[]} props.searchKeys
 * @param {string} props.searchKeys[].title
 * @param {string} props.searchKeys[].field
 * @param {boolean} props.searchKeys[].active
 *
 * @params {Object[]} props.filterForm
 * @params {string} props.filterForm[].title
 * @params {string} props.filterForm[]._type
 * @params {any} props.filterForm[]._wrapper
 * @params {boolean} props.filterForm[]._hidden
 *
 * @param {requestCallback} props.renderHeader
 * @param {requestCallback} props.renderRow
 * @param {string} props.title
 *
 * @param {string} props.backend
 * @param {requestCallback} props.handleBackendError
 *
 * @param {requestCallback} props.onRefresh
 * @param {Object[]} props.data
 */
export default function DataTable(props) {
  const {
    headCells: defaultHeadCells,
    backend,
    handleBackendError,
    data = [],
    changeData = (item) => {return item},
    onRefresh,
    searchKeys: defaultSearchKeys,
    orderBy,
    handleChangeSearchBar = () => {},
    order,
    ...other
  } = props;

  const [loading, setLoading] = useState();
  const [searchKeys, setSearchKeys] = useState(defaultSearchKeys);
  const [headCells, setHeadCells] = useState(defaultHeadCells);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  // const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rows, setRows] = useState(data);
  const [sort, setSort] = useState(
    order || {
      order: "DESC",
      orderBy: orderBy || headCells[0].field,
    }
  );
  const { rowsPerPage = 10 } = useSelector((state) => state.app);
  const dispatch = useDispatch();

  const generalProps = {
    handleChangeSearchBar,
    searchKeys,
    searchKeysChange: (keys) => {
      setSearchKeys(keys);
    },
    headCellsChange: setHeadCells,
    headCells,
    loading,
    setLoading,
    sort,
    setSort,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage: (num) => dispatch(setRowsPerPage(num)),
    searchText,
    setSearchText,
    rows,
    setRows,
    ...other,
  };
  const refresh = async () => {
    setLoading(true);
    await onRefresh(setLoading);
    setLoading(false);
  };
  if (backend) {
    return (
      <Online
        backend={backend}
        changeData={changeData}
        handleBackendError={handleBackendError}
        {...generalProps}
      />
    );
  }
  return (
    <Offline
      data={data}
      onRefresh={onRefresh ? refresh : undefined}
      {...generalProps}
    />
  );
}

export function DefaultTable(props){
    return <Table {...props}/>
};

function Online(props) {
  const {
    backend,
    changeData = null,
    handleBackendError,
    setLoading,
    sort,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    searchText,
    setSearchText,
    setRows,
    searchKeys,
    filterForm,
    ...other
  } = props;
  const [extra, setExtra] = useState();
  const [totalRows, setTotalRows] = useState(0);
  const [_searchText, _setSearchText] = useState(searchText || "");
  const [filterFormValues, setFilterFormValues] = useState(() => {
    if (filterForm && !filterForm.initialValues) {
      filterForm.initialValues = {};
      filterForm.form.forEach((el) => {
        const val = el._empty === undefined ? "" : el._empty;
        delete el._empty;
        filterForm.initialValues[el.name] = val;
      });
    }
    return filterForm?.initialValues;
  });

  useDebouncedEffect(() => _setSearchText(searchText), 500, [searchText]);

  useDidUpdateEffect(() => {
    getData(true);
  }, [
    sort,
    page,
    rowsPerPage,
    searchKeys,
    _searchText,
    backend,
    filterFormValues,
  ]);
  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (filterForm) {
      filterForm.handleSubmit = (values) => {
        setFilterFormValues(values);
      };
    }
  }, [filterForm]);

  const getData = async (skipExtra) => {
    if (!backend) return;
    setLoading(true);
    const { order, orderBy } = sort;
    const filterFields = [];
    if (searchKeys) {
      searchKeys.forEach((k) => {
        if (k.active) filterFields.push(k.field);
      });
    }

    const { data, error } = await httpService.get(backend, {
      params: {
        page,
        pageSize: rowsPerPage,
        orderBy,
        order,
        search: _searchText,
        filterFields,
        // filter: filterFormValues,
        ...filterFormValues,
        getExtra: skipExtra !== true,
      },
    });
    if (error) {
      setLoading(false);
      if (!handleBackendError) return;
      return handleBackendError(error, data);
    }

    if(changeData){
        setRows(changeData(data.items));
    }
    else{
        setRows(data.items);
    }
    if (data.extra) setExtra(data.extra);
    setPage(data.page);
    setRowsPerPage(data.pageSize);
    setTotalRows(data.totalItems);
    setLoading(false);
    return data;
  };

  return (
    <Table
      {...{
        setSearchText,
        sort,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        totalRows,
        onRefresh: getData,
        searchKeys,
        extra,
        filterForm,
        ...other,
      }}
    />
  );
}
function Offline(props) {
  const {
    data,
    sort,
    page,
    setPage,
    rowsPerPage,
    searchText,
    rows,
    setRows,
    searchKeys,
    onRefresh,
    handleChangeSearchBar =() => {},
    ...other
  } = props;
  const [filteredRows, setFilteredRows] = useState([]);
  const [sortedRows, setSortedRows] = useState([]);

  useEffect(() => {
    if (!searchText) {
      setFilteredRows(data);
      return;
    }
    const newData = data.filter((item) => {
      handleChangeSearchBar(searchText);

      if (!searchKeys || !searchKeys.length) {
        return JSON.stringify(item).indexOf(searchText) !== -1;
      }
      return (
        searchKeys
          .filter((k) => k.active)
          .findIndex((key) => {
            let value = deepFind(item, key.field);
            if (typeof value !== "string") {
              value = JSON.stringify(value) || "";
            }
            return value.indexOf(searchText) !== -1;
          }) !== -1
      );
    });
    if (
      newData.length !== rows.length ||
      JSON.stringify(newData) !== JSON.stringify(rows)
    ) {
      setFilteredRows(newData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchKeys, searchText]);

  useEffect(() => {
    if (!filteredRows.length && !sortedRows.length) return;
    const newRows = stableSort(filteredRows, sort.order, sort.orderBy);
    setPage(1);
    setSortedRows(newRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, sort]);

  useEffect(() => {
    if (!sortedRows.length && !rows.length) return;
    const start = (page - 1) * rowsPerPage;
    const newRowsInPage = sortedRows.slice(start, start + rowsPerPage);
    setRows(newRowsInPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedRows, page, rowsPerPage]);

  return (
    <Table
      {...{
        searchText,
        rows,
        sort,
        page,
        setPage,
        rowsPerPage,
        totalRows: data.length,
        onRefresh,
        searchKeys,
        ...other,
      }}
    />
  );
}
